
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { BlogPost } from "./useBlogPosts";
import { LinkedInPost, LinkedInPostStatus, LinkedInMedia } from "@/types/linkedin";
import { Json } from "@/integrations/supabase/types";

// Define the content types for the calendar
export type ContentType = 'all' | 'blog' | 'linkedin';

// Combined content item interface
export interface CalendarContentItem {
  id: string;
  title: string;
  content: string | null;
  status: string;
  scheduledFor: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  sourceType: 'blog' | 'linkedin';
  sourceData: BlogPost | LinkedInPost;
  image?: string | null;
}

interface ContentCalendarFilter {
  status?: string;
  category?: string;
}

interface UseContentCalendarParams {
  startDate: Date;
  endDate: Date;
  contentType: ContentType;
  filters?: ContentCalendarFilter;
}

export function useContentCalendar({
  startDate,
  endDate,
  contentType = 'all',
  filters = {}
}: UseContentCalendarParams) {
  return useQuery({
    queryKey: ["content-calendar", format(startDate, "yyyy-MM-dd"), format(endDate, "yyyy-MM-dd"), contentType, filters.status, filters.category],
    queryFn: async () => {
      try {
        // Format dates for queries
        const startDateStr = format(startDate, "yyyy-MM-dd");
        const endDateStr = format(endDate, "yyyy-MM-dd");
        
        // Determine which content to fetch based on contentType
        const fetchBlogPosts = contentType === 'all' || contentType === 'blog';
        const fetchLinkedInPosts = contentType === 'all' || contentType === 'linkedin';
        
        // Fetch blog posts if needed
        let blogPosts: BlogPost[] = [];
        if (fetchBlogPosts) {
          // Start building the query for blog posts
          let blogQuery = supabase
            .from("blog_posts")
            .select("*");
          
          // Create a complex filter for date range
          blogQuery = blogQuery.or(
            `scheduled_for.gte.${startDateStr},scheduled_for.lte.${endDateStr},` +
            `published_at.gte.${startDateStr},published_at.lte.${endDateStr}`
          );
          
          // Apply status filter if provided and not "all"
          if (filters.status && filters.status !== "all") {
            // Fix: Cast the status to the correct type for BlogPost
            blogQuery = blogQuery.eq("status", filters.status as "draft" | "published" | "scheduled");
          }
          
          // Apply category filter if provided
          if (filters.category && filters.category !== "all") {
            blogQuery = blogQuery.filter("metadata->categories", "cs", `["${filters.category}"]`);
          }
          
          // Execute blog posts query
          const { data: blogData, error: blogError } = await blogQuery;
          
          if (blogError) {
            throw blogError;
          }
          
          blogPosts = blogData as BlogPost[];
        }
        
        // Fetch LinkedIn posts if needed
        let linkedInPosts: LinkedInPost[] = [];
        if (fetchLinkedInPosts) {
          // Start building the query for LinkedIn posts
          let linkedInQuery = supabase
            .from("linkedin_posts")
            .select("*");
          
          // Create a filter for date range
          linkedInQuery = linkedInQuery.or(
            `scheduled_at.gte.${startDateStr},scheduled_at.lte.${endDateStr},` +
            `published_at.gte.${startDateStr},published_at.lte.${endDateStr}`
          );
          
          // Apply status filter if provided and not "all"
          if (filters.status && filters.status !== "all") {
            linkedInQuery = linkedInQuery.eq("status", filters.status);
          }
          
          // Execute LinkedIn posts query
          const { data: linkedInData, error: linkedInError } = await linkedInQuery;
          
          if (linkedInError) {
            throw linkedInError;
          }
          
          // Fix: Transform the raw data to match the LinkedInPost type
          linkedInPosts = linkedInData.map(post => {
            // Process the media_url field to ensure it's in the correct format
            let mediaUrls: LinkedInMedia[] = [];
            
            if (post.media_url) {
              try {
                // Handle different possible formats of media_url
                if (Array.isArray(post.media_url)) {
                  // If it's already an array, ensure each item has the correct shape
                  mediaUrls = (post.media_url as Json[]).map(item => {
                    const jsonItem = item as Record<string, unknown>;
                    return {
                      url: typeof jsonItem?.url === 'string' ? jsonItem.url : '',
                      type: (typeof jsonItem?.type === 'string' ? jsonItem.type : 'image') as 'image' | 'video',
                      id: typeof jsonItem?.id === 'string' ? jsonItem.id : undefined
                    };
                  });
                } else if (typeof post.media_url === 'string' && post.media_url.trim() !== '') {
                  // Parse JSON string
                  const parsedMedia = JSON.parse(post.media_url) as Record<string, unknown>[];
                  if (Array.isArray(parsedMedia)) {
                    mediaUrls = parsedMedia.map(item => ({
                      url: typeof item.url === 'string' ? item.url : '',
                      type: (typeof item.type === 'string' ? item.type : 'image') as 'image' | 'video',
                      id: typeof item.id === 'string' ? item.id : undefined
                    }));
                  }
                }
              } catch (e) {
                console.error("Error parsing media_url:", e);
                mediaUrls = [];
              }
            }
            
            return {
              ...post,
              media_url: mediaUrls,
              post_type: post.post_type as LinkedInPost['post_type'],
              status: post.status as LinkedInPostStatus
            } as LinkedInPost;
          });
        }
        
        // Transform blog posts to unified calendar content format
        const blogCalendarItems: CalendarContentItem[] = blogPosts.map(post => ({
          id: post.id,
          title: post.title,
          content: post.content,
          status: post.status,
          scheduledFor: post.scheduled_for,
          publishedAt: post.published_at,
          createdAt: post.created_at,
          updatedAt: post.updated_at,
          sourceType: 'blog',
          sourceData: post,
          image: post.image_url
        }));
        
        // Transform LinkedIn posts to unified calendar content format
        const linkedInCalendarItems: CalendarContentItem[] = linkedInPosts.map(post => {
          // For LinkedIn posts, we need to create a title from the content
          const title = post.content_text 
            ? (post.content_text.length > 50 
                ? post.content_text.substring(0, 50) + '...' 
                : post.content_text)
            : 'Untitled LinkedIn Post';
          
          // Get the first media URL if available
          const firstMedia = post.media_url && post.media_url.length > 0 ? post.media_url[0]?.url : null;
          
          return {
            id: post.id,
            title,
            content: post.content_text,
            status: post.status,
            scheduledFor: post.scheduled_at,
            publishedAt: post.published_at,
            createdAt: post.created_at || '',
            updatedAt: post.updated_at || '',
            sourceType: 'linkedin',
            sourceData: post,
            image: firstMedia
          };
        });
        
        // Combine all content items
        const allCalendarItems = [...blogCalendarItems, ...linkedInCalendarItems];
        
        return {
          items: allCalendarItems,
          counts: {
            total: allCalendarItems.length,
            blog: blogCalendarItems.length,
            linkedin: linkedInCalendarItems.length
          }
        };
      } catch (error: any) {
        console.error("Error fetching content calendar data:", error);
        toast.error("Failed to load content calendar");
        throw error;
      }
    },
    staleTime: 60000, // 1 minute
  });
}
